























import { Component, Mixins, Prop, Watch } from 'vue-property-decorator';
import { LocaleMixin } from '@/locales/locale-mixin';
import { ApiRequestMixin } from '@/core/mixins/api-request-mixin';
import { getModule } from 'vuex-module-decorators';
import { Change, UpdateSortKeys, UpdateSortParameter } from '@/families/models/update';
import { UpdatesRepository } from '@/families/repositories/updates-repository';
import { LoadingStore } from '@/store/loading-store';
import type { Family } from '@/families/models/family';
import AuditTrailTable from '@/families/components/AuditTrailTable.vue';
import { EventTypes } from '@/constants/event-type-constants';
import { HistoryTypes } from '@/constants/history-type-constants';
import { DataTableOptions } from '@/models/datatables';
import { getPagination, getSortingFromOptions } from '@/core/datatables-utils';
import { SortConstants } from '@/constants/sort-constants';

const historyRepo = new UpdatesRepository();
const loadingStore = getModule(LoadingStore);

@Component({
    components: { AuditTrailTable }
})
export default class FamilyActivityHistory extends Mixins(ApiRequestMixin, LocaleMixin) {
    @Prop({ required: true }) family!: Family;
    @Prop({ default: HistoryTypes.AUDIT_TRAIL }) readonly tableType!: string;

    private isFullAudit = true;
    private loadingKey = 'familyActivityHistory';
    private updates: Array<Change> = [];
    private updatedEvent = EventTypes.UPDATED;
    private totalCount = 0;
    private isLoading = false;
    private search = '';
    private actualSearch = '';
    private tableOptions: DataTableOptions = {
        page: 1,
        itemsPerPage: 10,
        sortBy: ['date'],
        sortDesc: [true]
    };

    private defaultSorting: UpdateSortParameter = {
        sort_keys: [UpdateSortKeys.DATE],
        sort_dir: [SortConstants.DESCENDING]
    };

    /**
     * Get the history items to show.
     */
    private get familyHistory(): Array<Change> {
        return this.updates;
    }

    /**
     * Get the name of the primary guardian of the family.
     */
    private get guardianName(): string {
        return this.family.primary_guardian.first_name + ' ' + this.family.primary_guardian.last_name;
    }

    @Watch('family', { deep: true })
    private watchFamilyChange() {
        this.resetPagination();
        this.loadHistory();
    }

    @Watch('tableOptions', { deep: true })
    private watchTableOptions() {
        if (!this.isLoading) {
            this.loadHistory();
        }
    }

    @Watch('isFullAudit')
    private watchIsFullAudit() {
        this.resetPagination();
        this.loadHistory();
    }

    @Watch('actualSearch')
    private async watchActualSearch() {
        this.resetPagination();
        await this.loadHistory();
    }

    private async created() {
        await this.loadHistory();
    }

    private clearSearch() {
        this.search = '';
        this.actualSearch = '';
    }

    private doSearch() {
        if (this.search.length >= 2) {
            this.actualSearch = this.search;
        } else {
            this.actualSearch = '';
        }
    }

    /**
     * Reset pagination to first page
     */
    private resetPagination() {
        this.tableOptions = {
            ...this.tableOptions,
            page: 1
        };
    }

    /**
     * Load the history for the current family.
     */
    private async loadHistory() {
        if (this.isLoading) {
            return;
        }

        this.isLoading = true;
        loadingStore.loadingIncrement(this.loadingKey);
        this.setAbortController();

        const pagination = getPagination(this.tableOptions);
        const sort = getSortingFromOptions(this.tableOptions, this.defaultSorting);
        const filterComments = this.tableType === HistoryTypes.COMMENTS;

        const response = await historyRepo.getAllUpdatesForFamily(
            this.family.id,
            pagination,
            sort,
            filterComments,
            this.isFullAudit,
            this.actualSearch,
            this.abortController
        );
        this.updates = response.entities;
        this.totalCount = Number(response.count);

        // Make sure we're not stranded on an invalid page
        const maxPage = Math.ceil(this.totalCount / this.tableOptions.itemsPerPage);
        if (this.tableOptions.page > maxPage) {
            this.tableOptions.page = Math.max(1, maxPage);
        }

        this.isLoading = false;
        loadingStore.loadingDecrement(this.loadingKey);
        this.clearAbortController();
    }

    private updateFamily() {
        this.$emit(EventTypes.UPDATED);
    }
}
